import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { 
  DollarSign, Clock, Tag, Plus, Trash2, Edit2, Save, X, 
  ArrowLeft, RefreshCcw, Globe, Percent, Calendar, Users,
  CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pricing');
  
  // Pricing State
  const [pricingSettings, setPricingSettings] = useState({
    regions: [],
    default_region: 'EU'
  });
  const [editingRegion, setEditingRegion] = useState(null);
  const [showRegionDialog, setShowRegionDialog] = useState(false);
  const [newRegion, setNewRegion] = useState({
    region: '',
    currency: 'EUR',
    monthly_price: 9.99,
    yearly_price: 99.99,
    countries: []
  });
  
  // Trial State
  const [trialSettings, setTrialSettings] = useState({
    duration_value: 14,
    duration_unit: 'days'
  });
  
  // Coupon State
  const [coupons, setCoupons] = useState([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_percent: 10,
    validity_type: 'forever',
    validity_value: null,
    validity_end_date: '',
    usage_limit_type: 'unlimited',
    max_uses: null,
    is_active: true,
    description: ''
  });

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [pricingRes, trialRes, couponsRes] = await Promise.all([
        axios.get(`${API}/admin/settings/pricing`, { headers }),
        axios.get(`${API}/admin/settings/trial`, { headers }),
        axios.get(`${API}/admin/coupons`, { headers })
      ]);
      
      setPricingSettings(pricingRes.data);
      setTrialSettings(trialRes.data);
      setCoupons(couponsRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/app/admin');
      }
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Pricing Functions
  const handleSavePricing = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/settings/pricing`,
        pricingSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pricing settings saved successfully');
    } catch (error) {
      toast.error('Failed to save pricing settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRegion = () => {
    if (!newRegion.region) {
      toast.error('Region code is required');
      return;
    }
    
    const exists = pricingSettings.regions.find(r => r.region === newRegion.region);
    if (exists) {
      toast.error('Region already exists');
      return;
    }
    
    setPricingSettings(prev => ({
      ...prev,
      regions: [...prev.regions, { ...newRegion, countries: newRegion.countries || [] }]
    }));
    
    setNewRegion({
      region: '',
      currency: 'EUR',
      monthly_price: 9.99,
      yearly_price: 99.99,
      countries: []
    });
    setShowRegionDialog(false);
    toast.success('Region added. Remember to save changes.');
  };

  const handleUpdateRegion = (index, field, value) => {
    setPricingSettings(prev => {
      const regions = [...prev.regions];
      regions[index] = { ...regions[index], [field]: value };
      return { ...prev, regions };
    });
  };

  const handleDeleteRegion = (index) => {
    setPricingSettings(prev => ({
      ...prev,
      regions: prev.regions.filter((_, i) => i !== index)
    }));
    toast.success('Region removed. Remember to save changes.');
  };

  // Trial Functions
  const handleSaveTrial = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/settings/trial`,
        trialSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Trial settings saved successfully');
    } catch (error) {
      toast.error('Failed to save trial settings');
    } finally {
      setSaving(false);
    }
  };

  // Coupon Functions
  const handleCreateCoupon = async () => {
    if (!newCoupon.code) {
      toast.error('Coupon code is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const couponData = {
        ...newCoupon,
        validity_value: newCoupon.validity_type === 'days' || newCoupon.validity_type === 'months' 
          ? parseInt(newCoupon.validity_value) || null 
          : null,
        max_uses: newCoupon.usage_limit_type === 'max_uses' 
          ? parseInt(newCoupon.max_uses) || null 
          : null
      };
      
      if (editingCoupon) {
        await axios.put(
          `${API}/admin/coupons/${editingCoupon.coupon_id}`,
          couponData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coupon updated successfully');
      } else {
        await axios.post(
          `${API}/admin/coupons`,
          couponData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coupon created successfully');
      }
      
      setShowCouponDialog(false);
      setEditingCoupon(null);
      resetCouponForm();
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setNewCoupon({
      code: coupon.code,
      discount_percent: coupon.discount_percent,
      validity_type: coupon.validity_type || 'forever',
      validity_value: coupon.validity_value,
      validity_end_date: coupon.validity_end_date || '',
      usage_limit_type: coupon.usage_limit_type || 'unlimited',
      max_uses: coupon.max_uses,
      is_active: coupon.is_active,
      description: coupon.description || ''
    });
    setShowCouponDialog(true);
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API}/admin/coupons/${couponId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Coupon deleted successfully');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/coupons/${couponId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Coupon status updated');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to update coupon status');
    }
  };

  const resetCouponForm = () => {
    setNewCoupon({
      code: '',
      discount_percent: 10,
      validity_type: 'forever',
      validity_value: null,
      validity_end_date: '',
      usage_limit_type: 'unlimited',
      max_uses: null,
      is_active: true,
      description: ''
    });
  };

  const currencies = ['EUR', 'USD', 'GBP', 'INR', 'SEK', 'NOK', 'DKK', 'CHF', 'AUD', 'CAD', 'JPY'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-2">
          <RefreshCcw className="w-5 h-5 animate-spin" />
          <span>Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/admin/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="flex flex-col items-start">
              <h1 className="text-xl font-bold">Admin Settings</h1>
              <p className="text-xs text-muted-foreground">Manage pricing, trials, and coupons</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAllSettings}>
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md mb-6">
            <TabsTrigger value="pricing" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="trial" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Free Trial
            </TabsTrigger>
            <TabsTrigger value="coupons" className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Coupons
            </TabsTrigger>
          </TabsList>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      Regional Pricing
                    </CardTitle>
                    <CardDescription>
                      Set different prices for different regions and currencies
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowRegionDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Region
                    </Button>
                    <Button onClick={handleSavePricing} disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Label>Default Region</Label>
                  <Select 
                    value={pricingSettings.default_region} 
                    onValueChange={(value) => setPricingSettings(prev => ({ ...prev, default_region: value }))}
                  >
                    <SelectTrigger className="w-48 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pricingSettings.regions.map(r => (
                        <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Region</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Monthly Price</TableHead>
                      <TableHead>Yearly Price</TableHead>
                      <TableHead>Countries</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingSettings.regions.map((region, index) => (
                      <TableRow key={region.region}>
                        <TableCell>
                          <Badge variant="outline">{region.region}</Badge>
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={region.currency} 
                            onValueChange={(value) => handleUpdateRegion(index, 'currency', value)}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {currencies.map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            step="0.01"
                            value={region.monthly_price}
                            onChange={(e) => handleUpdateRegion(index, 'monthly_price', parseFloat(e.target.value))}
                            className="w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            step="0.01"
                            value={region.yearly_price}
                            onChange={(e) => handleUpdateRegion(index, 'yearly_price', parseFloat(e.target.value))}
                            className="w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">
                            {region.countries?.length || 0} countries
                          </span>
                        </TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Region?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will remove the {region.region} pricing region.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteRegion(index)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trial Tab */}
          <TabsContent value="trial">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Free Trial Duration
                </CardTitle>
                <CardDescription>
                  Configure how long new users can try the platform for free
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4 max-w-md">
                  <div className="flex-1">
                    <Label>Duration</Label>
                    <Input 
                      type="number" 
                      min="1"
                      value={trialSettings.duration_value}
                      onChange={(e) => setTrialSettings(prev => ({ 
                        ...prev, 
                        duration_value: parseInt(e.target.value) || 1 
                      }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Unit</Label>
                    <Select 
                      value={trialSettings.duration_unit} 
                      onValueChange={(value) => setTrialSettings(prev => ({ ...prev, duration_unit: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="days">Days</SelectItem>
                        <SelectItem value="weeks">Weeks</SelectItem>
                        <SelectItem value="months">Months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">
                    New users will get <strong>{trialSettings.duration_value} {trialSettings.duration_unit}</strong> of free trial 
                    when they register.
                  </p>
                </div>
                
                <Button onClick={handleSaveTrial} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Trial Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Tag className="w-5 h-5" />
                      Coupon Codes
                    </CardTitle>
                    <CardDescription>
                      Create and manage promotional discount codes
                    </CardDescription>
                  </div>
                  <Button onClick={() => { resetCouponForm(); setEditingCoupon(null); setShowCouponDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Coupon
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Validity</TableHead>
                      <TableHead>Usage Limit</TableHead>
                      <TableHead>Used</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coupons.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No coupons created yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      coupons.map((coupon) => (
                        <TableRow key={coupon.coupon_id}>
                          <TableCell>
                            <code className="bg-muted px-2 py-1 rounded font-mono text-sm">
                              {coupon.code}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <Percent className="w-3 h-3" />
                              {coupon.discount_percent}%
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {coupon.validity_type === 'forever' && 'Forever'}
                              {coupon.validity_type === 'days' && `${coupon.validity_value} days`}
                              {coupon.validity_type === 'months' && `${coupon.validity_value} months`}
                              {coupon.validity_type === 'fixed_date' && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(coupon.validity_end_date).toLocaleDateString()}
                                </span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {coupon.usage_limit_type === 'unlimited' && 'Unlimited'}
                              {coupon.usage_limit_type === 'max_uses' && `Max ${coupon.max_uses}`}
                              {coupon.usage_limit_type === 'one_per_user' && 'One per user'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              <Users className="w-3 h-3 mr-1" />
                              {coupon.usage_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={coupon.is_active ? 'default' : 'secondary'}
                              className="cursor-pointer"
                              onClick={() => handleToggleCoupon(coupon.coupon_id)}
                            >
                              {coupon.is_active ? (
                                <><CheckCircle className="w-3 h-3 mr-1" /> Active</>
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" /> Inactive</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditCoupon(coupon)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-destructive">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Coupon?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete the coupon code &quot;{coupon.code}&quot;.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteCoupon(coupon.coupon_id)}>
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Region Dialog */}
      <Dialog open={showRegionDialog} onOpenChange={setShowRegionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Pricing Region</DialogTitle>
            <DialogDescription>
              Create a new pricing region with custom currency and prices
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Region Code</Label>
                <Input 
                  placeholder="e.g., EU, US, IN"
                  value={newRegion.region}
                  onChange={(e) => setNewRegion(prev => ({ ...prev, region: e.target.value.toUpperCase() }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select 
                  value={newRegion.currency} 
                  onValueChange={(value) => setNewRegion(prev => ({ ...prev, currency: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Price</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newRegion.monthly_price}
                  onChange={(e) => setNewRegion(prev => ({ ...prev, monthly_price: parseFloat(e.target.value) }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Yearly Price</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newRegion.yearly_price}
                  onChange={(e) => setNewRegion(prev => ({ ...prev, yearly_price: parseFloat(e.target.value) }))}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegionDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRegion}>Add Region</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
            <DialogDescription>
              {editingCoupon ? 'Update coupon settings' : 'Create a new promotional coupon code'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Coupon Code *</Label>
                <Input 
                  placeholder="e.g., SUMMER25"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Discount Percentage *</Label>
                <div className="relative mt-1">
                  <Input 
                    type="number"
                    min="1"
                    max="100"
                    value={newCoupon.discount_percent}
                    onChange={(e) => setNewCoupon(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) }))}
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea 
                placeholder="Internal note about this coupon..."
                value={newCoupon.description}
                onChange={(e) => setNewCoupon(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-base font-medium">Validity Settings</Label>
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-sm">Discount Duration</Label>
                  <Select 
                    value={newCoupon.validity_type} 
                    onValueChange={(value) => setNewCoupon(prev => ({ ...prev, validity_type: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="forever">Forever (no expiry)</SelectItem>
                      <SelectItem value="days">Fixed number of days</SelectItem>
                      <SelectItem value="months">Fixed number of months</SelectItem>
                      <SelectItem value="fixed_date">Until specific date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {(newCoupon.validity_type === 'days' || newCoupon.validity_type === 'months') && (
                  <div>
                    <Label className="text-sm">Duration Value</Label>
                    <Input 
                      type="number"
                      min="1"
                      placeholder={`Number of ${newCoupon.validity_type}`}
                      value={newCoupon.validity_value || ''}
                      onChange={(e) => setNewCoupon(prev => ({ ...prev, validity_value: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                )}
                
                {newCoupon.validity_type === 'fixed_date' && (
                  <div>
                    <Label className="text-sm">End Date</Label>
                    <Input 
                      type="date"
                      value={newCoupon.validity_end_date}
                      onChange={(e) => setNewCoupon(prev => ({ ...prev, validity_end_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-base font-medium">Usage Limits</Label>
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-sm">Limit Type</Label>
                  <Select 
                    value={newCoupon.usage_limit_type} 
                    onValueChange={(value) => setNewCoupon(prev => ({ ...prev, usage_limit_type: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unlimited">Unlimited uses</SelectItem>
                      <SelectItem value="max_uses">Maximum total uses</SelectItem>
                      <SelectItem value="one_per_user">One use per user</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {newCoupon.usage_limit_type === 'max_uses' && (
                  <div>
                    <Label className="text-sm">Maximum Uses</Label>
                    <Input 
                      type="number"
                      min="1"
                      placeholder="e.g., 100"
                      value={newCoupon.max_uses || ''}
                      onChange={(e) => setNewCoupon(prev => ({ ...prev, max_uses: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between border-t pt-4">
              <div>
                <Label>Active Status</Label>
                <p className="text-xs text-muted-foreground">Enable or disable this coupon</p>
              </div>
              <Switch 
                checked={newCoupon.is_active}
                onCheckedChange={(checked) => setNewCoupon(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCouponDialog(false); setEditingCoupon(null); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateCoupon} disabled={saving}>
              {saving ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
